import React, { useState, useEffect, useCallback } from 'react'
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core'
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { FaGripLines, FaSave, FaTimes, FaInfoCircle } from 'react-icons/fa'
import { languageUtil } from '../../util/languageUtils'
import { API_URL } from '../../constants'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { LoadingOverlay } from '../../shared/components/Loading'

// Sortable Item Component - Moved outside to prevent re-renders
const SortableItem = React.memo(({ item, getItemImage, getItemTitle, showId }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: item.id,
    })

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    }

    const imageUrl = getItemImage(item)
    const itemTitle = getItemTitle(item)

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`flex items-center p-4 border-b border-gray-200 last:border-b-0 transition-colors ${
                isDragging ? 'bg-blue-100 shadow-lg opacity-80 z-50' : 'bg-white hover:bg-gray-50'
            }`}
        >
            <div
                {...attributes}
                {...listeners}
                className='p-2 mr-4 text-gray-400 cursor-grab active:cursor-grabbing'
            >
                <FaGripLines size={18} />
            </div>

            <div className='flex items-center flex-1 gap-4'>
                {showId && (
                    <span className='bg-gray-100 text-gray-600 text-xs font-medium px-2.5 py-1 rounded-full min-w-[2rem] text-center'>
                        {item.id}
                    </span>
                )}

                {imageUrl ? (
                    <img
                        src={imageUrl}
                        alt={itemTitle}
                        className='object-cover w-16 h-12 rounded'
                        onError={e => {
                            e.target.style.display = 'none'
                            const parent = e.target.parentElement
                            if (parent) {
                                const placeholder = document.createElement('div')
                                placeholder.className =
                                    'flex items-center justify-center w-16 h-12 text-gray-400 bg-gray-100 rounded'
                                placeholder.textContent = 'No image'
                                parent.appendChild(placeholder)
                            }
                        }}
                    />
                ) : (
                    <div className='flex items-center justify-center w-16 h-12 text-gray-400 bg-gray-100 rounded'>
                        No image
                    </div>
                )}

                <span className='flex-1 font-medium text-gray-800'>{itemTitle}</span>
            </div>
        </div>
    )
})

SortableItem.displayName = 'SortableItem'

// Main ReorderPage Component
const ReorderPage = ({
    token = localStorage.getItem('token'),
    fetchEndpoint,
    reorderEndpoint,
    title = 'Reorder Items',
    successEndpoint,
    onReorderSuccess,
    onReorderError,
    getItemImage = item => item.header_image || '',
    getItemTitle = item => item.title,
    showId = false,
    customHeaders = {},
    fetchMethod = 'GET',
    fetchBody = null,
}) => {
    const [items, setItems] = useState([])
    const [originalOrder, setOriginalOrder] = useState([])
    const [isDirty, setIsDirty] = useState(false)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState(null)

    // Get language once and memoize it
    const { language } = languageUtil()
    const { t } = useTranslation()

    const navigate = useNavigate()

    // Memoize callbacks to prevent unnecessary re-renders
    const memoizedGetItemImage = useCallback(getItemImage, [])
    const memoizedGetItemTitle = useCallback(getItemTitle, [])

    // Configure sensors for drag and drop
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    )

    // Memoize the fetch function
    const fetchData = useCallback(async () => {
        if (!fetchEndpoint) {
            console.error('fetchEndpoint is required')
            setLoading(false)
            return
        }

        try {
            setLoading(true)
            setError(null)

            const headers = {
                Authorization: `Bearer ${token}`,
                'Accept-Language': language,
                ...customHeaders,
            }

            if (fetchMethod === 'POST') {
                headers['Content-Type'] = 'application/json'
            }

            const options = {
                method: fetchMethod,
                headers,
            }

            if (fetchMethod === 'POST' && fetchBody) {
                options.body = JSON.stringify(fetchBody)
            }

            const response = await fetch(`${API_URL}${fetchEndpoint}`, options)

            if (!response.ok) {
                throw new Error(`Failed to fetch: ${response.status}`)
            }

            const json = await response.json()

            if (json.success && Array.isArray(json.data)) {
                const validItems = json.data.filter(item => item && item.id !== undefined)
                setItems(validItems)
                setOriginalOrder(validItems.map(item => item.id))
            } else if (Array.isArray(json)) {
                const validItems = json.filter(item => item && item.id !== undefined)
                setItems(validItems)
                setOriginalOrder(validItems.map(item => item.id))
            } else {
                console.error('Invalid data structure:', json)
                setError('Invalid data format received from server')
            }
        } catch (error) {
            console.error('Failed to fetch data:', error)
            setError(error.message || 'Failed to load data')
        } finally {
            setLoading(false)
        }
    }, [fetchEndpoint, token, language, customHeaders, fetchMethod, fetchBody])

    // Fetch data on mount only
    useEffect(() => {
        fetchData()
    }, []) // Only re-run if fetchData changes

    const handleDragEnd = useCallback(event => {
        const { active, over } = event

        if (active.id !== over.id) {
            setItems(items => {
                const oldIndex = items.findIndex(item => item.id === active.id)
                const newIndex = items.findIndex(item => item.id === over.id)

                const newItems = arrayMove(items, oldIndex, newIndex)
                setIsDirty(true)
                return newItems
            })
        }
    }, [])

    const handleSave = useCallback(async () => {
        if (!token) {
            alert('Authentication token is required')
            return
        }

        if (!reorderEndpoint) {
            alert('Reorder endpoint is required')
            return
        }

        const newOrder = items.map(item => item.id)

        // Check if order actually changed
        if (JSON.stringify(newOrder) === JSON.stringify(originalOrder)) {
            alert('No changes to save')
            setIsDirty(false)
            return
        }

        setSaving(true)

        try {
            const response = await fetch(`${API_URL}${reorderEndpoint}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                    'Accept-Language': language,
                },
                body: JSON.stringify({ order: newOrder }),
            })

            if (!response.ok) {
                const errorText = await response.text()
                throw new Error(`Failed to save order: ${response.status} - ${errorText}`)
            }

            const result = await response.json()

            // Update original order to match new order
            setOriginalOrder(newOrder)
            setIsDirty(false)

            if (result.success) {
                alert('Order saved successfully!')
                if (successEndpoint) {
                    navigate(successEndpoint)
                }
                if (onReorderSuccess) {
                    onReorderSuccess(newOrder, result)
                }
            } else {
                throw new Error(result.message || 'Failed to save order')
            }
        } catch (error) {
            const errorMessage = error.message || 'Failed to save order'
            alert(errorMessage)

            if (onReorderError) {
                onReorderError(error)
            }
        } finally {
            setSaving(false)
        }
    }, [items, originalOrder, reorderEndpoint, token, language, onReorderSuccess, onReorderError])

    const handleReset = useCallback(() => {
        // Reset to original fetched order
        const originalItemsMap = {}
        items.forEach(item => {
            originalItemsMap[item.id] = item
        })

        const resetItems = originalOrder
            .filter(id => originalItemsMap[id])
            .map(id => originalItemsMap[id])

        setItems(resetItems)
        setIsDirty(false)
    }, [items, originalOrder])

    const refreshData = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const headers = {
                Authorization: `Bearer ${token}`,
                'Accept-Language': language,
                ...customHeaders,
            }

            const options = {
                method: fetchMethod,
                headers,
            }

            if (fetchMethod === 'POST' && fetchBody) {
                options.body = JSON.stringify(fetchBody)
            }

            const response = await fetch(`${API_URL}${fetchEndpoint}`, options)
            const json = await response.json()

            if (json.success && Array.isArray(json.data)) {
                const validItems = json.data.filter(item => item && item.id !== undefined)
                setItems(validItems)
                setOriginalOrder(validItems.map(item => item.id))
                setIsDirty(false)
            }
        } catch (error) {
            console.error('Failed to refresh:', error)
            setError(error.message || 'Failed to refresh data')
        } finally {
            setLoading(false)
        }
    }, [fetchEndpoint, token, language, customHeaders, fetchMethod, fetchBody])

    if (loading) {
        return <LoadingOverlay />
    }

    if (error) {
        return (
            <div className='max-w-4xl p-6 mx-auto'>
                <div className='p-4 text-center rounded-lg bg-red-50'>
                    <h2 className='mb-2 text-lg font-semibold text-red-700'>Error Loading Data</h2>
                    <p className='text-red-600'>{error}</p>
                    <button
                        onClick={refreshData}
                        className='px-4 py-2 mt-4 text-white bg-red-600 rounded-md hover:bg-red-700'
                    >
                        Retry
                    </button>
                </div>
            </div>
        )
    }

    if (items.length === 0) {
        return (
            <div className='max-w-4xl p-6 mx-auto'>
                <div className='py-12 text-center rounded-lg bg-gray-50'>
                    <h3 className='text-lg font-medium text-gray-700'>No items to reorder</h3>
                    <p className='mt-2 text-gray-500'>No items found for reordering.</p>
                    <button
                        onClick={refreshData}
                        className='px-4 py-2 mt-4 text-blue-600 bg-blue-100 rounded-md hover:bg-blue-200'
                    >
                        Refresh
                    </button>
                </div>
            </div>
        )
    }

    return (
        <div className='w-full p-6 mx-auto bg-white'>
            <div className='flex flex-col items-start justify-between pb-4 mb-8 border-b border-gray-200 sm:flex-row sm:items-center'>
                <div>
                    <h1 className='mb-2 text-2xl font-bold text-gray-800'>{title}</h1>
                    <p className='text-sm text-gray-500'>
                        {t('reorder.total')} {items.length} {isDirty && '• Unsaved changes'}
                    </p>
                </div>

                <div className='flex gap-3 mt-4 sm:mt-0'>
                    <button
                        onClick={handleReset}
                        disabled={!isDirty}
                        className={`flex items-center gap-2 px-4 py-2 rounded-md transition-colors ${
                            isDirty
                                ? 'text-gray-700 border border-gray-300 hover:bg-gray-50'
                                : 'text-gray-400 border border-gray-200 cursor-not-allowed'
                        }`}
                    >
                        <FaTimes /> {t('reorder.reset')}
                    </button>

                    <button
                        onClick={handleSave}
                        disabled={!isDirty || saving}
                        className={`flex items-center gap-2 px-4 py-2 rounded-md transition-colors ${
                            isDirty && !saving
                                ? 'bg-blue-600 text-white hover:bg-blue-700'
                                : 'bg-blue-400 text-white cursor-not-allowed'
                        }`}
                    >
                        {saving ? (
                            <>
                                <div className='w-4 h-4 border-2 border-white rounded-full border-t-transparent animate-spin'></div>
                                {t('status.saving')}
                            </>
                        ) : (
                            <>
                                <FaSave /> {t('reorder.save_order')}
                            </>
                        )}
                    </button>
                </div>
            </div>

            <div className='p-4 mb-4 border border-blue-100 rounded-lg bg-blue-50'>
                <div className='flex items-start gap-3'>
                    <FaInfoCircle className='flex-shrink-0 mt-1 text-blue-500' />
                    <p className='text-sm text-blue-700'>
                        {t('reorder.info_part1')}
                        <FaGripLines className='inline mx-1' />
                        {t('reorder.info_part2')}
                        <span className='font-semibold'>{t('reorder.save_order')}</span>
                        {t('reorder.info_part3')}
                    </p>
                </div>
            </div>

            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
            >
                <SortableContext
                    items={items.map(item => item.id)}
                    strategy={verticalListSortingStrategy}
                >
                    <div className='overflow-hidden bg-white border-gray-300 rounded-lg shadow-lg'>
                        {items.map((item, index) => (
                            <SortableItem
                                key={item.id}
                                item={item}
                                index={index}
                                getItemImage={memoizedGetItemImage}
                                getItemTitle={memoizedGetItemTitle}
                                showId={showId}
                            />
                        ))}
                    </div>
                </SortableContext>
            </DndContext>
        </div>
    )
}

export default React.memo(ReorderPage)
